import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import EditIcon from "@mui/icons-material/Edit";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  fetchRedirects,
  createRedirect,
  updateRedirect,
  deleteRedirect,
  refreshRedirects,
} from "../../api/marketing";
import { getErrorMessage } from "../../api/generic";

const SAVE_DELAY_MS = 1000;
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const STATUS_ICONS = {
  new: {
    icon: FiberNewIcon,
    color: "success.main",
    label: "New - not yet deployed",
  },
  modified: {
    icon: EditIcon,
    color: "warning.main",
    label: "Modified - not yet deployed",
  },
  unchanged: {
    icon: CloudDoneIcon,
    color: "text.disabled",
    label: "Matches the astro repo",
  },
};

function StatusIcon({ status }) {
  const statusInfo = STATUS_ICONS[status];
  if (!statusInfo) return null;
  const Icon = statusInfo.icon;
  return (
    <Tooltip title={statusInfo.label}>
      <Icon
        fontSize="small"
        sx={{ color: statusInfo.color, display: "block" }}
      />
    </Tooltip>
  );
}

function isValidRelativeUri(value) {
  return (
    !!value &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("://") &&
    !value.includes("\\")
  );
}

function isValidRedirectTarget(value) {
  return isValidRelativeUri(value) || /^https?:\/\//i.test(value || "");
}

export default function RedirectsEditor({ refreshKey = 0 }) {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newRedirect, setNewRedirect] = useState({
    source: "",
    destination: "",
  });
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const saveTimeouts = useRef({});

  const filteredRedirects = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return redirects;
    return redirects.filter(
      (redirect) =>
        redirect.source.toLowerCase().includes(query) ||
        redirect.destination.toLowerCase().includes(query)
    );
  }, [redirects, searchText]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredRedirects.length / rowsPerPage)
  );

  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  const paginatedRedirects = filteredRedirects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    setLoading(true);
    fetchRedirects(
      (data) => {
        setRedirects(data);
        setLoading(false);
      },
      (error) => {
        setError(getErrorMessage(error));
        setLoading(false);
      }
    );
  }, [refreshKey]);

  useEffect(() => {
    const timeouts = saveTimeouts.current;
    return () => {
      Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const handleFieldChange = (redirect, fieldName, value) => {
    const updatedRedirect = { ...redirect, [fieldName]: value };
    setRedirects((current) =>
      current.map((r) => (r.id === redirect.id ? updatedRedirect : r))
    );

    if (saveTimeouts.current[redirect.id]) {
      clearTimeout(saveTimeouts.current[redirect.id]);
    }
    saveTimeouts.current[redirect.id] = setTimeout(() => {
      updateRedirect(
        redirect.id,
        {
          source: updatedRedirect.source,
          destination: updatedRedirect.destination,
        },
        (saved) => {
          setError("");
          setRedirects((current) =>
            current.map((r) => (r.id === saved.id ? saved : r))
          );
        },
        (error) => setError(getErrorMessage(error))
      );
    }, SAVE_DELAY_MS);
  };

  const handleDelete = (redirect) => {
    if (saveTimeouts.current[redirect.id]) {
      clearTimeout(saveTimeouts.current[redirect.id]);
    }
    deleteRedirect(
      redirect.id,
      () => {
        setError("");
        setRedirects((current) => current.filter((r) => r.id !== redirect.id));
      },
      (error) => setError(getErrorMessage(error))
    );
  };

  const handleAdd = () => {
    if (!isValidRelativeUri(newRedirect.source)) {
      setError("Source must be a relative URI, e.g. /old-page.");
      return;
    }
    if (!isValidRedirectTarget(newRedirect.destination)) {
      setError(
        "Destination must be a relative URI or an http(s) URL, e.g. /old-page or https://example.com."
      );
      return;
    }
    createRedirect(
      newRedirect.source,
      newRedirect.destination,
      (created) => {
        setError("");
        setRedirects((current) => [...current, created]);
        setNewRedirect({ source: "", destination: "" });
      },
      (error) => setError(getErrorMessage(error))
    );
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    setRefreshConfirmOpen(false);
    Object.values(saveTimeouts.current).forEach((timeout) =>
      clearTimeout(timeout)
    );
    saveTimeouts.current = {};

    setRefreshing(true);
    refreshRedirects(
      (data) => {
        setError("");
        setRedirects(data);
        setPage(0);
        setRefreshing(false);
      },
      (error) => {
        setError(getErrorMessage(error));
        setRefreshing(false);
      }
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Box flexGrow={1} my={2}>
          <FormControl fullWidth variant="outlined">
            <TextField
              label="Search"
              value={searchText}
              onChange={(event) => handleSearchChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <IconButton onClick={() => handleSearchChange("")}>
                    <HighlightOffIcon />
                  </IconButton>
                ) : null,
              }}
            />
          </FormControl>
        </Box>
        <Tooltip title="Refresh from the astro repo">
          <span>
            <IconButton
              size="small"
              disabled={refreshing}
              onClick={() => setRefreshConfirmOpen(true)}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {error && (
        <Alert severity="error" sx={{ my: 1 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Source</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="/old-page"
                  value={newRedirect.source}
                  onChange={(event) =>
                    setNewRedirect({
                      ...newRedirect,
                      source: event.target.value,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="/new-page"
                  value={newRedirect.destination}
                  onChange={(event) =>
                    setNewRedirect({
                      ...newRedirect,
                      destination: event.target.value,
                    })
                  }
                />
              </TableCell>
              <TableCell padding="checkbox">
                <IconButton size="small" color="primary" onClick={handleAdd}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            {redirects.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ color: "text.secondary", py: 3 }}
                >
                  No redirects yet. Click the refresh button above to import the
                  current redirects from the astro repo.
                </TableCell>
              </TableRow>
            )}
            {paginatedRedirects.map((redirect) => (
              <TableRow key={redirect.id}>
                <TableCell padding="checkbox">
                  <StatusIcon status={redirect.status} />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={redirect.source}
                    onChange={(event) =>
                      handleFieldChange(redirect, "source", event.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={redirect.destination}
                    onChange={(event) =>
                      handleFieldChange(
                        redirect,
                        "destination",
                        event.target.value
                      )
                    }
                  />
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(redirect)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredRedirects.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
      <Dialog
        open={refreshConfirmOpen}
        onClose={() => setRefreshConfirmOpen(false)}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Refresh
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will refetch the redirects and replace all in-progress edits.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefreshConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
